import { useState, useEffect, useCallback, useRef } from 'react';
import { useAgoraRTM } from '../context/AgoraRTMContext';

/**
 * useAgoraPubSub - A pub/sub hook that mirrors VideoSDK's usePubSub
 * Uses AgoraRTMContext for real network messaging between participants.
 * Supports { persist: true } via Agora RTM channel attributes so late
 * joiners receive the most recent persisted message for each topic.
 */

// Global event emitter for local messages across all hook instances
// This ensures that when one component publishes, ALL subscribers receive the local message
const localMessageListeners = new Map(); // Map<topic, Set<callback>>

function subscribeToLocalMessages(topic, callback) {
  if (!localMessageListeners.has(topic)) {
    localMessageListeners.set(topic, new Set());
  }
  localMessageListeners.get(topic).add(callback);

  // Return unsubscribe function
  return () => {
    const listeners = localMessageListeners.get(topic);
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) {
        localMessageListeners.delete(topic);
      }
    }
  };
}

function emitLocalMessage(topic, payload) {
  const listeners = localMessageListeners.get(topic);
  if (listeners) {
    listeners.forEach(callback => {
      try {
        callback(payload);
      } catch (e) {
        console.error(`[PubSub] Error in local message listener for topic ${topic}:`, e);
      }
    });
  }
}

/**
 * useAgoraPubSub hook
 * @param {string} topic - The topic to subscribe to
 * @param {object} options - Options including onMessageReceived callback
 * @returns {object} - { publish, messages, isReady }
 */
export function useAgoraPubSub(topic, options = {}) {
  const { onMessageReceived } = options;
  const [messages, setMessages] = useState([]);
  const onMessageReceivedRef = useRef(onMessageReceived);
  const rtm = useAgoraRTM();
  const persistedLoadedRef = useRef(false);

  // Update ref when callback changes
  useEffect(() => {
    onMessageReceivedRef.current = onMessageReceived;
  }, [onMessageReceived]);

  // Store rtm in a ref for use in publish callback (to avoid stale closure)
  const rtmRef = useRef(rtm);
  useEffect(() => {
    rtmRef.current = rtm;
  }, [rtm]);

  // Subscribe to LOCAL messages (from other hook instances on the same client)
  // This is separate from RTM and ensures all subscribers get local messages immediately
  const MAX_MESSAGES = 50; // Cap message history to prevent unbounded memory growth
  useEffect(() => {
    const handleLocalMessage = (payload) => {
      setMessages(prev => {
        const next = [...prev, payload];
        return next.length > MAX_MESSAGES ? next.slice(-MAX_MESSAGES) : next;
      });
      onMessageReceivedRef.current?.(payload);
    };

    const unsubscribe = subscribeToLocalMessages(topic, handleLocalMessage);
    return unsubscribe;
  }, [topic]);

  // Subscribe to command topic via RTM (for messages from OTHER participants)
  // IMPORTANT: Only re-subscribe when topic changes or RTM becomes available.
  // DO NOT depend on the full `rtm` object — it changes on every message due to
  // `messages` in its useMemo deps, causing massive unsubscribe/resubscribe churn.
  // subscribeToCommand is a stable useCallback([]) so it's safe to use from rtmRef.
  const isRealContext = rtm?.isRealContext;
  const subscribeToCommand = rtm?.subscribeToCommand;
  useEffect(() => {
    if (!isRealContext || !subscribeToCommand) {
      return;
    }

    const unsubscribe = subscribeToCommand(topic, (payload) => {
      // Skip if this is our own message (we already handled it via local emit)
      const currentRtm = rtmRef.current;
      const localUserId = currentRtm?.localUserId ? String(currentRtm.localUserId) : null;
      if (localUserId && payload.senderId === localUserId) {
        return;
      }
      setMessages(prev => {
        const next = [...prev, payload];
        return next.length > MAX_MESSAGES ? next.slice(-MAX_MESSAGES) : next;
      });
      onMessageReceivedRef.current?.(payload);
    });

    return () => {
      unsubscribe();
    };
  }, [topic, isRealContext, subscribeToCommand]);

  // Fetch persisted message from channel attributes when RTM becomes ready (late joiner support)
  useEffect(() => {
    if (!rtm?.isRealContext || !rtm?.isChannelReady || !rtm?.getChannelAttributes) {
      return;
    }
    // Only load persisted data once per mount
    if (persistedLoadedRef.current) {
      return;
    }
    persistedLoadedRef.current = true;

    const loadPersistedMessage = async () => {
      try {
        if (!rtm?.isChannelReady) return;
        const attrs = await rtm.getChannelAttributes();
        const attrKey = `pubsub_${topic}`;
        if (attrs[attrKey]?.value) {
          let persistedData = JSON.parse(attrs[attrKey].value);
          // Handle previously double-serialized values (string instead of object)
          if (typeof persistedData === 'string') {
            try {
              persistedData = JSON.parse(persistedData);
            } catch (e) {
              // Not valid JSON string - skip
              console.warn(`[PubSub] Could not parse double-serialized persisted data for topic ${topic}`);
              return;
            }
          }

          // Check if the persisted message is a "clear" message - if so, skip it
          try {
            const parsedMessage = typeof persistedData.message === 'string'
              ? JSON.parse(persistedData.message)
              : persistedData.message;
            if (parsedMessage?.clear) {
              return;
            }
          } catch (e) {
            // Not JSON or no clear flag - proceed normally
          }

          // Replay the persisted message as if it just arrived
          const payload = {
            message: persistedData.message,
            senderId: persistedData.senderId || 'unknown',
            senderName: persistedData.senderName || 'Unknown',
            timestamp: persistedData.timestamp || Date.now(),
            topic,
            isPersisted: true,
          };
          setMessages(prev => [...prev, payload]);
          onMessageReceivedRef.current?.(payload);
        }
      } catch (error) {
        console.error(`[PubSub] Failed to load persisted message for topic ${topic}:`, error);
      }
    };

    loadPersistedMessage();
  }, [topic, rtm?.isRealContext, rtm?.isChannelReady]);

  // Publish function - sends via RTM to all participants
  // Supports { persist: true } to store message as channel attribute for late joiners
  const publish = useCallback(async (message, publishOptions = {}) => {

    // Get current rtm from ref to avoid stale closure
    const currentRtm = rtmRef.current;

    // Get local user info from RTM context for the local emit
    const localUserId = currentRtm?.localUserId ? String(currentRtm.localUserId) : 'local';
    const localUserName = currentRtm?.localUserName || publishOptions.senderName || 'You';
    const timestamp = Date.now();

    // ALWAYS emit locally first so the sender sees their own action immediately
    const localPayload = {
      message,
      senderId: localUserId,
      senderName: localUserName,
      timestamp,
      topic,
      isLocal: true,
    };

    // Emit to ALL local subscribers (all hook instances on this client subscribed to this topic)
    emitLocalMessage(topic, localPayload);

    // Send via RTM to other participants
    // NOTE: Don't gate on isChannelReady here — sendCommand() has its own
    // queueing mechanism that buffers commands and flushes them when RTM
    // connects. This ensures mute commands are not silently dropped.
    if (currentRtm?.isRealContext && currentRtm?.sendCommand) {
      try {
        const success = await currentRtm.sendCommand(topic, message);
        if (success) {
        } else {
          console.warn(`[PubSub] RTM sendCommand returned false for topic ${topic} (may be queued)`);
        }
      } catch (error) {
        console.error(`[PubSub] Error sending to RTM on topic ${topic}:`, error);
      }
    } else {
      console.warn(`[PubSub] RTM not available for topic ${topic}, message only emitted locally`);
    }

    // Persist the message as a channel attribute if { persist: true }
    if (publishOptions.persist && currentRtm?.isRealContext && currentRtm?.setChannelAttribute) {
      try {
        // Check if this is a "clear" message - if so, delete the attribute instead
        let isClear = false;
        try {
          const parsed = typeof message === 'string' ? JSON.parse(message) : message;
          isClear = !!parsed?.clear;
        } catch (e) {
          // Not JSON - not a clear message
        }

        const attrKey = `pubsub_${topic}`;
        if (isClear && currentRtm?.deleteChannelAttribute) {
          await currentRtm.deleteChannelAttribute(attrKey);
        } else {
          await currentRtm.setChannelAttribute(attrKey, {
            message,
            senderId: localUserId,
            senderName: localUserName,
            timestamp,
          });
        }
      } catch (error) {
        console.error(`[PubSub] Failed to persist message for topic ${topic}:`, error);
      }
    } else if (publishOptions.persist) {
      console.warn(`[PubSub] Cannot persist for topic ${topic}: RTM not ready`);
    }
  }, [topic]);

  // Compute isReady based on current rtm state
  const isReady = (rtm?.isRealContext && rtm?.isChannelReady) || false;

  return {
    publish,
    messages,
    isReady,
  };
}

// Legacy exports for backward compatibility
export function initializePubSubRTM() {}
export function cleanupPubSubRTM() {}

export default useAgoraPubSub;
